import { apiErrorMessage } from '@/api/client';
import { ConfigButton } from '@/components/admin/ConfigButton';
import { INPUT, MenuCheck, Tip, authorKind, normalizeText, useDismiss } from '@/components/admin/ui';
import { ConfirmModal } from '@/components/common/Modal';
import {
  ChevronDownIcon,
  CloseIcon,
  PagePrevIcon,
  PageNextIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '@/components/common/icons';
import type { AdminData } from '@/hooks/useAdminData';
import { ITEM_TYPES, type CategoryModel, type ItemModel, type ItemType } from '@/types/categories.types';
import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface ItemsViewProps {
  data: AdminData;
  /** The caller's username, for "Autor: vy". */
  me: string;
  onOpenEditor: (item: ItemModel) => void;
}

/** The category filter value for items in no category. */
const NO_CATEGORY = '__none';
const PER_PAGE_OPTIONS = [10, 20, 50];
const TABLE_GRID =
  'grid grid-cols-[minmax(90px,150px)_minmax(100px,150px)_minmax(150px,1.2fr)_86px_minmax(0,2fr)_30px] gap-3.5';

const MENU =
  'absolute top-[calc(100%+6px)] z-40 border border-control bg-white py-1.5 shadow-[0_10px_28px_rgba(11,30,50,.16)]';
const MENU_ROW =
  'flex w-full cursor-pointer items-center gap-[9px] border-none bg-transparent px-3 py-[7px] text-left font-[inherit] text-xs text-ink hover:bg-[#f2f7fb]';
const DONE_BUTTON =
  'cursor-pointer rounded border-none bg-primary px-[11px] py-[5px] text-[11.5px] font-semibold text-white hover:bg-primary-deep';

const filterButton = (active: boolean): string =>
  `flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded border px-2.5 py-[7px] text-xs hover:border-primary ${
    active ? 'border-primary bg-accent-soft font-semibold text-primary' : 'border-control bg-white font-normal text-ink'
  }`;

const pageButton = (on: boolean, disabled: boolean): string =>
  `flex h-7 min-w-7 items-center justify-center rounded border px-2 text-xs font-semibold ${
    on
      ? 'cursor-pointer border-primary bg-primary text-white'
      : disabled
        ? 'cursor-default border-control bg-white text-dash'
        : 'cursor-pointer border-control bg-white text-muted'
  }`;

const toggleIn = <T,>(list: T[], value: T): T[] =>
  list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

/** A filter button with its checkbox menu, "Vymazat" and "Hotovo". */
const FilterMenu: FC<{
  label: string;
  active: boolean;
  width: number;
  onClear: () => void;
  children: ReactNode;
}> = ({ label, active, width, onClear, children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={filterButton(active)}
      >
        {label}
        <ChevronDownIcon size={11} strokeWidth={2.4} />
      </button>
      {open ? (
        <div role="menu" aria-label={label} className={`${MENU} right-0`} style={{ width }}>
          {children}
          <div className="mt-1 flex justify-between border-t border-hairline px-2.5 pb-0.5 pt-1.5">
            <button
              type="button"
              onClick={onClear}
              className="cursor-pointer border-none bg-transparent px-1 py-[5px] text-[11.5px] font-semibold text-muted hover:text-primary"
            >
              {t('admin.items.clear')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className={DONE_BUTTON}>
              {t('admin.items.done')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** The row's category chips with the "+ Kategorie" picker. */
const CategoryCell: FC<{
  item: ItemModel;
  categories: CategoryModel[];
  onToggle: (key: string, member: boolean) => void;
}> = ({ item, categories, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="relative flex flex-wrap items-center gap-[5px]">
      {item.categories.map((key) => {
        const category = categories.find((entry) => entry.key === key);
        if (!category) return null;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-[5px] py-[3px] pl-[7px] pr-1.5 text-[11px] font-semibold text-ink"
            style={{ background: category.tint }}
          >
            <span className="inline-block h-[7px] w-[7px] flex-none" style={{ background: category.color }} />
            {category.label_cs}
            <button
              type="button"
              onClick={() => onToggle(key, false)}
              aria-label={`${t('admin.items.removeCategory')} ${category.label_cs}`}
              className="ml-0.5 flex cursor-pointer items-center border-none bg-transparent p-0 text-label-2 hover:text-danger"
            >
              <CloseIcon size={11} strokeWidth={2.4} />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex cursor-pointer items-center gap-1 rounded border border-dashed border-dash bg-white px-[7px] py-[3px] text-[11px] font-semibold text-muted hover:border-primary hover:text-primary"
      >
        <PlusIcon size={11} strokeWidth={2.4} />
        {t('admin.items.addCategory')}
      </button>
      {open ? (
        <div role="menu" aria-label={t('admin.items.colCategories')} className={`${MENU} left-0 z-30 w-[270px]`}>
          {categories.map((category) => {
            const on = item.categories.includes(category.key);
            return (
              <button
                key={category.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={on}
                onClick={() => onToggle(category.key, !on)}
                className={MENU_ROW}
              >
                <MenuCheck on={on} />
                <span className="inline-block h-2 w-2 flex-none" style={{ background: category.color }} />
                <span>{category.label_cs}</span>
              </button>
            );
          })}
          <div className="mt-1 flex justify-end border-t border-hairline px-2.5 pb-0.5 pt-1.5">
            <button type="button" onClick={() => setOpen(false)} className={DONE_BUTTON}>
              {t('admin.items.done')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * "Podle položek": the table of every item with search, category and type filters, pagination,
 * inline category membership, type select, the configuration button and delete.
 *
 * Locks follow `editable` from the API: a locked item keeps its type select disabled, opens its
 * configuration read-only and cannot be deleted; category membership stays open to every admin.
 */
export const ItemsView: FC<ItemsViewProps> = ({ data, me, onOpenEditor }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<ItemType[]>([]);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState<ItemModel | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // A new item is listed last (the backend's creation order): show it by clearing the filters
  // and turning to the last page, as the prototype shows its new item at once.
  const itemCount = data.items.length;
  const previousCount = useRef(itemCount);
  useEffect(() => {
    if (itemCount > previousCount.current) {
      setQuery('');
      setCatFilter([]);
      setTypeFilter([]);
      setPage(Math.ceil(itemCount / perPage));
    }
    previousCount.current = itemCount;
  }, [itemCount, perPage]);

  const q = normalizeText(query.trim());
  const filtered = data.items.filter(
    (item) =>
      (!q || normalizeText(item.name).includes(q) || normalizeText(item.id).includes(q)) &&
      (!catFilter.length ||
        catFilter.some((key) =>
          key === NO_CATEGORY ? item.categories.length === 0 : item.categories.includes(key)
        )) &&
      (!typeFilter.length || typeFilter.includes(item.type))
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const current = Math.min(page, pageCount);
  const paged = filtered.slice((current - 1) * perPage, current * perPage);

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('admin.saveFailed')));
    }
  };

  const catLabel = (key: string) =>
    key === NO_CATEGORY ? t('admin.items.noCategory') : data.categories.find((entry) => entry.key === key)?.label_cs;
  const catButtonLabel = !catFilter.length
    ? t('admin.items.allCategories')
    : catFilter.length === 1
      ? (catLabel(catFilter[0]) ?? '')
      : t('admin.items.categoriesCount', { count: catFilter.length });
  const typeButtonLabel = !typeFilter.length
    ? t('admin.items.allTypes')
    : t('admin.items.typesLabel', { list: typeFilter.map((type) => t(`admin.types.${type}`)).join(', ') });
  const anyFilter = !!query || catFilter.length > 0 || typeFilter.length > 0;

  const summary = t('admin.items.summary', {
    range: filtered.length ? `${(current - 1) * perPage + 1}–${(current - 1) * perPage + paged.length}` : '0',
    shown: filtered.length,
    total: filtered.length !== data.items.length ? t('admin.items.summaryTotal', { count: data.items.length }) : '',
    multi: data.items.filter((item) => item.categories.length > 1).length,
    none: data.items.filter((item) => item.categories.length === 0).length,
  });

  const catOptions = [
    ...data.categories.map((entry) => ({ key: entry.key, label: entry.label_cs, color: entry.color })),
    { key: NO_CATEGORY, label: t('admin.items.noCategory'), color: '#b9c8d6' },
  ];

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0 flex-[1_1_320px]">
          <h1 className="m-0 mb-[5px] text-xl font-bold text-primary">{t('admin.items.title')}</h1>
          <p className="m-0 max-w-[680px] text-[12.5px] leading-[1.55] text-muted">{t('admin.items.intro')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex items-center">
            <SearchIcon
              size={13}
              strokeWidth={2.2}
              stroke="#6d7f91"
              className="pointer-events-none absolute left-[9px]"
            />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder={t('admin.items.search')}
              aria-label={t('admin.items.search')}
              className={`${INPUT} w-[210px] py-[7px] pl-7 pr-2.5 text-xs`}
            />
          </div>
          <FilterMenu
            label={catButtonLabel}
            active={catFilter.length > 0}
            width={270}
            onClear={() => {
              setCatFilter([]);
              setPage(1);
            }}
          >
            {catOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={catFilter.includes(option.key)}
                onClick={() => {
                  setCatFilter(toggleIn(catFilter, option.key));
                  setPage(1);
                }}
                className={MENU_ROW}
              >
                <MenuCheck on={catFilter.includes(option.key)} />
                <span className="inline-block h-2 w-2 flex-none" style={{ background: option.color }} />
                <span>{option.label}</span>
              </button>
            ))}
          </FilterMenu>
          <FilterMenu
            label={typeButtonLabel}
            active={typeFilter.length > 0}
            width={170}
            onClear={() => {
              setTypeFilter([]);
              setPage(1);
            }}
          >
            {ITEM_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                role="menuitemcheckbox"
                aria-checked={typeFilter.includes(type)}
                onClick={() => {
                  setTypeFilter(toggleIn(typeFilter, type));
                  setPage(1);
                }}
                className={MENU_ROW}
              >
                <MenuCheck on={typeFilter.includes(type)} />
                <span>{t(`admin.types.${type}`)}</span>
              </button>
            ))}
          </FilterMenu>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setCatFilter([]);
              setTypeFilter([]);
              setPage(1);
            }}
            className={`block cursor-pointer border-none bg-transparent px-1 py-[7px] text-xs font-semibold text-muted hover:text-primary ${
              anyFilter ? 'visible' : 'invisible'
            }`}
          >
            {t('admin.items.clearFilter')}
          </button>
        </div>
      </div>

      <div className="mt-[18px] border border-line bg-white">
        <div
          className={`${TABLE_GRID} border-b border-line bg-wash px-3.5 py-[9px] text-[10px] font-semibold uppercase tracking-[.08em] text-label-2`}
        >
          <span>{t('admin.items.colId')}</span>
          <span>{t('admin.items.colName')}</span>
          <span>{t('admin.items.colCategories')}</span>
          <span>{t('admin.items.colType')}</span>
          <span>{t('admin.items.colConfig')}</span>
          <span />
        </div>
        {paged.map((item) => {
          const kind = authorKind(item.author, me);
          return (
            <div key={item.id} className={`${TABLE_GRID} items-start border-b border-hairline px-3.5 py-2.5`}>
              <div className="flex min-w-0 flex-col gap-[3px] pt-[5px]">
                <span className="break-all font-mono text-[11px] text-ink">{item.id}</span>
                <span className="text-[10.5px]" style={{ color: kind === 'me' ? '#0a5c48' : '#93a5b5' }}>
                  {t(`admin.author.${kind}`, { name: item.author })}
                </span>
              </div>
              <span className="pt-1 text-[12.5px] font-semibold text-ink">{item.name}</span>
              <CategoryCell
                item={item}
                categories={data.categories}
                onToggle={(key, member) => run(() => data.setItemCategory(item.id, key, member))}
              />
              <select
                value={item.type}
                disabled={!item.editable}
                aria-label={`${t('admin.items.colType')}: ${item.name}`}
                onChange={(event) => run(() => data.updateItem(item.id, { type: event.target.value as ItemType }))}
                className={`w-full rounded border border-control px-1.5 py-[5px] font-[inherit] text-xs font-semibold ${
                  item.editable ? 'cursor-pointer bg-white text-ink' : 'cursor-not-allowed bg-[#f3f6f9] text-label-2'
                }`}
              >
                {ITEM_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`admin.types.${type}`)}
                  </option>
                ))}
              </select>
              <ConfigButton item={item} tone="wash" onOpen={() => onOpenEditor(item)} />
              <Tip
                align="right"
                width="230px"
                content={item.editable ? t('admin.items.deleteTip') : t('admin.items.deleteTipLocked')}
              >
                <button
                  type="button"
                  aria-label={t('admin.items.delete')}
                  aria-disabled={!item.editable}
                  onClick={() => {
                    if (item.editable) setToDelete(item);
                  }}
                  className={`flex h-7 w-7 items-center justify-center rounded border bg-white p-0 ${
                    item.editable
                      ? 'cursor-pointer border-[#f0cdd4] text-danger hover:bg-danger-soft'
                      : 'cursor-not-allowed border-line text-[#c5d1dc]'
                  }`}
                >
                  <TrashIcon size={13} />
                </button>
              </Tip>
            </div>
          );
        })}
        {filtered.length === 0 ? (
          <div className="px-3.5 py-[22px] text-[12.5px] text-label-2">{t('admin.items.empty')}</div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-0.5 py-2.5">
        <div className="text-[11px] text-label-2">{summary}</div>
        <div className="flex flex-wrap items-center gap-2.5">
          <label className="flex items-center gap-1.5 text-[11.5px] text-muted">
            {t('admin.items.perPage')}
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="cursor-pointer rounded border border-control bg-white px-1.5 py-1 font-[inherit] text-[11.5px] text-ink"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('admin.items.prev')}
              disabled={current <= 1}
              onClick={() => setPage(current - 1)}
              className={pageButton(false, current <= 1)}
            >
              <PagePrevIcon size={12} strokeWidth={2.4} />
            </button>
            {Array.from({ length: pageCount }, (_, index) => index + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-current={n === current ? 'page' : undefined}
                onClick={() => setPage(n)}
                className={pageButton(n === current, false)}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              aria-label={t('admin.items.next')}
              disabled={current >= pageCount}
              onClick={() => setPage(current + 1)}
              className={pageButton(false, current >= pageCount)}
            >
              <PageNextIcon size={12} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>

      {toDelete ? (
        <ConfirmModal
          title={t('admin.items.deleteTitle')}
          text={t('admin.items.deleteText', {
            name: toDelete.name,
            id: toDelete.id,
            cats: toDelete.categories.length ? ` (${toDelete.categories.length})` : '',
          })}
          confirmLabel={t('admin.items.deleteOk')}
          cancelLabel={t('admin.system.cancel')}
          isBusy={isDeleting}
          onConfirm={async () => {
            setIsDeleting(true);
            await run(() => data.deleteItem(toDelete.id));
            setIsDeleting(false);
            setToDelete(null);
          }}
          onCancel={() => setToDelete(null)}
        />
      ) : null}
    </>
  );
};
